import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * "Metro map" signal flow.
 *
 * Five lines (Forecast, Commitment, Calendar, Analytics, Release Plan) depart
 * from stations, bend into a shared trunk, and ride to the Delivery Review
 * terminus. Reading order starts with Delivery Review to match the explorer's
 * default tab.
 *
 * RLX-FEAT-008 discipline:
 *   - Exactly ONE map in the DOM: desktop (horizontal) or mobile (vertical),
 *     chosen via matchMedia — not two SVGs toggled by CSS. SSR/prerender
 *     renders the desktop map.
 *   - SMIL + CSS animations pause while the map is offscreen
 *     (IntersectionObserver + SVG pauseAnimations / animation-play-state).
 *   - Line accents come from the violet-only CSS custom properties
 *     (--rlx-accent-*), so the legacy multi-accent palette stays restorable.
 *   - Each line carries a one-line caption in the legend under the map.
 */

type MetroLine = {
  id: string;
  label: string;
  color: string;
  station: { x: number; y: number };
  labelAt: { x: number; y: number };
  labelAnchor: 'start' | 'middle';
  /** Visible colored segment: station → trunk junction. */
  feeder: string;
  /** Full train route: station → junction → terminus. */
  route: string;
  junction: { x: number; y: number };
  dur: string;
  begin: string;
};

const DESKTOP_TRUNK = 'M 560 120 L 1130 120';
const DESKTOP_TERMINUS = { x: 1130, y: 120 } as const;

const DESKTOP_LINES: readonly MetroLine[] = [
  {
    id: 'forecast',
    label: 'Forecast',
    color: 'var(--rlx-accent-forecast)',
    station: { x: 130, y: 30 },
    labelAt: { x: 156, y: 20 },
    labelAnchor: 'start',
    feeder: 'M 130 30 L 470 30 L 570 120',
    route: 'M 130 30 L 470 30 L 570 120 L 1130 120',
    junction: { x: 570, y: 120 },
    dur: '10s',
    begin: '0s',
  },
  {
    id: 'commitment',
    label: 'Commitment',
    color: 'var(--rlx-accent-commitment)',
    station: { x: 130, y: 75 },
    labelAt: { x: 156, y: 65 },
    labelAnchor: 'start',
    feeder: 'M 130 75 L 540 75 L 630 120',
    route: 'M 130 75 L 540 75 L 630 120 L 1130 120',
    junction: { x: 630, y: 120 },
    dur: '11s',
    begin: '-2.5s',
  },
  {
    id: 'calendar',
    label: 'Calendar',
    color: 'var(--rlx-accent-calendar)',
    station: { x: 130, y: 120 },
    labelAt: { x: 156, y: 110 },
    labelAnchor: 'start',
    feeder: 'M 130 120 L 560 120',
    route: 'M 130 120 L 1130 120',
    junction: { x: 560, y: 120 },
    dur: '12s',
    begin: '-5s',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    color: 'var(--rlx-accent-analytics)',
    station: { x: 130, y: 165 },
    labelAt: { x: 156, y: 155 },
    labelAnchor: 'start',
    feeder: 'M 130 165 L 600 165 L 690 120',
    route: 'M 130 165 L 600 165 L 690 120 L 1130 120',
    junction: { x: 690, y: 120 },
    dur: '13s',
    begin: '-7.5s',
  },
  {
    id: 'release-plan',
    label: 'Release Plan',
    color: 'var(--rlx-accent-release)',
    station: { x: 130, y: 210 },
    labelAt: { x: 156, y: 200 },
    labelAnchor: 'start',
    feeder: 'M 130 210 L 660 210 L 750 120',
    route: 'M 130 210 L 660 210 L 750 120 L 1130 120',
    junction: { x: 750, y: 120 },
    dur: '14s',
    begin: '-10s',
  },
] as const;

const MOBILE_TRUNK = 'M 160 140 L 160 440';
const MOBILE_TERMINUS = { x: 160, y: 440 } as const;

/* Station labels zigzag between two top rows so neighbours never collide. */
const MOBILE_LINES: readonly MetroLine[] = [
  {
    id: 'forecast',
    label: 'Forecast',
    color: 'var(--rlx-accent-forecast)',
    station: { x: 30, y: 60 },
    labelAt: { x: 30, y: 32 },
    labelAnchor: 'middle',
    feeder: 'M 30 60 L 30 120 L 160 250',
    route: 'M 30 60 L 30 120 L 160 250 L 160 440',
    junction: { x: 160, y: 250 },
    dur: '10s',
    begin: '0s',
  },
  {
    id: 'commitment',
    label: 'Commitment',
    color: 'var(--rlx-accent-commitment)',
    station: { x: 95, y: 60 },
    labelAt: { x: 95, y: 18 },
    labelAnchor: 'middle',
    feeder: 'M 95 60 L 95 140 L 160 205',
    route: 'M 95 60 L 95 140 L 160 205 L 160 440',
    junction: { x: 160, y: 205 },
    dur: '11s',
    begin: '-2.5s',
  },
  {
    id: 'calendar',
    label: 'Calendar',
    color: 'var(--rlx-accent-calendar)',
    station: { x: 160, y: 60 },
    labelAt: { x: 160, y: 32 },
    labelAnchor: 'middle',
    feeder: 'M 160 60 L 160 140',
    route: 'M 160 60 L 160 440',
    junction: { x: 160, y: 140 },
    dur: '12s',
    begin: '-5s',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    color: 'var(--rlx-accent-analytics)',
    station: { x: 225, y: 60 },
    labelAt: { x: 225, y: 18 },
    labelAnchor: 'middle',
    feeder: 'M 225 60 L 225 175 L 160 240',
    route: 'M 225 60 L 225 175 L 160 240 L 160 440',
    junction: { x: 160, y: 240 },
    dur: '13s',
    begin: '-7.5s',
  },
  {
    id: 'release-plan',
    label: 'Release Plan',
    color: 'var(--rlx-accent-release)',
    station: { x: 290, y: 60 },
    labelAt: { x: 290, y: 32 },
    labelAnchor: 'middle',
    feeder: 'M 290 60 L 290 150 L 160 280',
    route: 'M 290 60 L 290 150 L 160 280 L 160 440',
    junction: { x: 160, y: 280 },
    dur: '14s',
    begin: '-10s',
  },
] as const;

/**
 * Line captions — reading order starts with the Delivery Review terminus,
 * then feeders in explorer-tab order.
 */
const LINE_CAPTIONS: readonly { id: string; label: string; caption: string; color: string }[] = [
  {
    id: 'delivery-review',
    label: 'Delivery Review',
    caption: 'Every signal lands here — as one prioritized decision queue.',
    color: 'var(--rlx-accent-review)',
  },
  {
    id: 'forecast',
    label: 'Forecast',
    caption: 'Planned scope vs real capacity.',
    color: 'var(--rlx-accent-forecast)',
  },
  {
    id: 'commitment',
    label: 'Commitment',
    caption: 'What changed after the promise.',
    color: 'var(--rlx-accent-commitment)',
  },
  {
    id: 'calendar',
    label: 'Calendar',
    caption: 'Real team availability.',
    color: 'var(--rlx-accent-calendar)',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    caption: 'What is driving the risk.',
    color: 'var(--rlx-accent-analytics)',
  },
  {
    id: 'release-plan',
    label: 'Release Plan',
    caption: 'Load across the release timeline.',
    color: 'var(--rlx-accent-release)',
  },
] as const;

const LABEL_STYLE: React.CSSProperties = {
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
};

const NARROW_QUERY = '(max-width: 900px)';

/** SSR-safe media query hook; the server snapshot is the desktop map. */
function useNarrowViewport(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {};
      }
      const mql = window.matchMedia(NARROW_QUERY);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    () =>
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(NARROW_QUERY).matches
        : false,
    () => false
  );
}

type MetroMapProps = {
  lines: readonly MetroLine[];
  trunkPath: string;
  terminus: { x: number; y: number };
  viewBox: string;
  className: string;
  labelFontSize: number;
};

function MetroMap({
  lines,
  trunkPath,
  terminus,
  viewBox,
  className,
  labelFontSize,
}: MetroMapProps): React.ReactElement {
  /* Single map in the DOM → gradient IDs never collide. */
  const lineGradientId = 'signalFlowLine';
  const dotGradientId = 'signalFlowDot';

  return (
    <svg
      className={className}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      focusable="false"
    >
      <defs>
        <linearGradient id={lineGradientId} x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="rgba(124, 92, 255, 0.18)" />
          <stop offset="50%" stopColor="rgba(220, 210, 255, 0.85)" />
          <stop offset="100%" stopColor="rgba(124, 92, 255, 0.18)" />
        </linearGradient>
        <radialGradient id={dotGradientId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(252, 248, 255, 1)" />
          <stop offset="60%" stopColor="rgba(200, 188, 255, 0.65)" />
          <stop offset="100%" stopColor="rgba(123, 92, 255, 0)" />
        </radialGradient>
      </defs>

      {lines.map((line) => (
        <path
          key={`${line.id}-glow`}
          d={line.feeder}
          stroke={line.color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity="0.12"
        />
      ))}

      <path
        d={trunkPath}
        stroke="rgba(124, 92, 255, 0.18)"
        strokeWidth="18"
        strokeLinecap="round"
        opacity="0.5"
        fill="none"
        className="signal-flow__halo"
      />
      <path
        d={trunkPath}
        stroke={`url(#${lineGradientId})`}
        strokeWidth="2.4"
        strokeLinecap="round"
        opacity="0.9"
        fill="none"
      />
      <path
        d={trunkPath}
        stroke="rgba(220, 210, 255, 0.55)"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
        strokeDasharray="2 18"
        className="signal-flow__rail-dash"
      />

      {lines.map((line) => (
        <path
          key={line.id}
          d={line.feeder}
          stroke={line.color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity="0.85"
        />
      ))}

      {lines.map((line) => (
        <circle
          key={`${line.id}-junction`}
          cx={line.junction.x}
          cy={line.junction.y}
          r="4.5"
          fill="rgba(5, 8, 22, 0.96)"
          stroke="rgba(232, 226, 255, 0.8)"
          strokeWidth="1.2"
        />
      ))}

      {lines.map((line) => (
        <g key={`${line.id}-station`}>
          <circle
            cx={line.station.x}
            cy={line.station.y}
            r="14"
            fill="rgba(5, 8, 22, 0.96)"
            stroke={line.color}
            strokeWidth="1.6"
          />
          <circle cx={line.station.x} cy={line.station.y} r="5" fill={line.color} />
          <text
            x={line.labelAt.x}
            y={line.labelAt.y}
            textAnchor={line.labelAnchor}
            fill="rgba(226, 220, 255, 0.9)"
            fontSize={labelFontSize}
            style={LABEL_STYLE}
          >
            {line.label}
          </text>
        </g>
      ))}

      <g>
        <circle
          cx={terminus.x}
          cy={terminus.y}
          r="18"
          fill="none"
          stroke="rgba(167, 139, 250, 0.5)"
          strokeWidth="1.4"
          className="signal-flow__terminus-pulse"
        >
          <animate attributeName="r" values="18;34" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle
          cx={terminus.x}
          cy={terminus.y}
          r="30"
          fill="rgba(123, 92, 255, 0.08)"
          stroke="rgba(167, 139, 250, 0.32)"
          strokeWidth="1"
        />
        <circle
          cx={terminus.x}
          cy={terminus.y}
          r="16"
          fill="rgba(5, 8, 22, 0.96)"
          stroke="rgba(220, 210, 255, 0.7)"
          strokeWidth="1.4"
        />
        <circle cx={terminus.x} cy={terminus.y} r="4" fill="rgba(252, 248, 255, 0.98)" />
        <text
          x={terminus.x}
          y={terminus.y + 52}
          textAnchor="middle"
          fill="rgba(200, 190, 235, 0.85)"
          fontSize="12"
          style={LABEL_STYLE}
        >
          Delivery Review
        </text>
      </g>

      {lines.map((line) => (
        <g key={`${line.id}-train`}>
          <circle r="12" fill={`url(#${dotGradientId})`} className="signal-flow__train-halo">
            <animateMotion
              dur={line.dur}
              repeatCount="indefinite"
              begin={line.begin}
              path={line.route}
              calcMode="linear"
            />
          </circle>
          <circle r="4.5" fill="rgba(252, 248, 255, 0.98)" className="signal-flow__train">
            <animateMotion
              dur={line.dur}
              repeatCount="indefinite"
              begin={line.begin}
              path={line.route}
              calcMode="linear"
            />
          </circle>
        </g>
      ))}
    </svg>
  );
}

export function SignalFlowRow(): React.ReactElement {
  const narrow = useNarrowViewport();
  const stageRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setInView(entry.isIntersecting);
        }
      },
      { threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* Pause SMIL (trains, terminus pulse) while offscreen; CSS animations follow
     via [data-paused] on the container. */
  useEffect(() => {
    const svg = stageRef.current?.querySelector<SVGSVGElement>('svg');
    if (!svg) return;
    if (inView) {
      svg.unpauseAnimations();
    } else {
      svg.pauseAnimations();
    }
  }, [inView, narrow]);

  return (
    <div
      className="signal-flow signal-flow--canonical"
      data-paused={inView ? undefined : 'true'}
      aria-label="Delivery Review is the terminus that receives signals from Forecast, Commitment, Calendar, Analytics, and Release Plan"
    >
      <div className="signal-flow__stage" ref={stageRef} aria-hidden="true">
        {narrow ? (
          <MetroMap
            lines={MOBILE_LINES}
            trunkPath={MOBILE_TRUNK}
            terminus={MOBILE_TERMINUS}
            viewBox="0 0 340 520"
            className="signal-flow__svg signal-flow__svg--mobile"
            labelFontSize={9.5}
          />
        ) : (
          <MetroMap
            lines={DESKTOP_LINES}
            trunkPath={DESKTOP_TRUNK}
            terminus={DESKTOP_TERMINUS}
            viewBox="0 0 1280 240"
            className="signal-flow__svg signal-flow__svg--desktop"
            labelFontSize={11}
          />
        )}
      </div>
      <ul className="signal-flow__captions" aria-label="What each line carries">
        {LINE_CAPTIONS.map((line) => (
          <li key={line.id} className="signal-flow__caption">
            <span
              className="signal-flow__caption-dot"
              style={{ backgroundColor: line.color }}
              aria-hidden="true"
            />
            <span className="signal-flow__caption-label">{line.label}</span>
            <span className="signal-flow__caption-text">{line.caption}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
